<template>
	<div class="title-ranking-list">
		<img
			v-if="withLogo && !hideRank"
			class="title-ranking-list__img"
			loading="lazy"
			:src="`${ASSETS}/img/jw-icon.png`"
			alt="JustWatch Logo"
		/>
		<p
			v-if="!hideRank"
			class="title-ranking-list__rank"
			:class="{ 'title-ranking-list__rank--condensed': withCondensed }"
		>
			{{ padNumber(rank) }}.
		</p>
		<div
			class="arrow-container"
			:class="{
				'arrow-container--up': trend === 'UP',
				'arrow-container--stable': trend === 'STABLE',
				'arrow-container--down': trend === 'DOWN',
			}"
			v-if="showArrow && !hideRank"
		>
			<FontAwesomeIcon :icon="faCaretUp" :class="arrowClasses" size="lg" />
			<p>{{ trendDifference }}</p>
		</div>
		<div v-else-if="isNew && !hideRank" class="new">
			{{ $t('WEBAPP_MENU_NEW') }}
		</div>
		<div v-if="showDetails" class="title-ranking-list__details">
			<span v-if="!hideRank">·</span>
			<p>{{ runtime }}</p>
			<span>·</span>
			<p>{{ props.releaseYear }}</p>
		</div>
	</div>
</template>

<script setup lang="ts">
import type { Maybe } from '@/helpers/types';
import { ModuleContentTitleTrend } from '@/@types/graphql-types';
import { formatRuntime } from '@/helpers/title-helper';
import { computed } from 'vue';
import { faCaretUp } from '@fortawesome/free-solid-svg-icons';

const { t } = useI18n();
interface SingleRankingDisplayProps {
	trend: ModuleContentTitleTrend;
	rank: number;
	trendDifference: number;
	daysInTop1000?: number;
	withLogo?: boolean;
	withCondensed?: boolean;
	runtime?: Maybe<number>;
	releaseYear?: Maybe<number>;
	hideRank?: boolean;
}
const props = withDefaults(defineProps<SingleRankingDisplayProps>(), {
	withLogo: true,
	withCondensed: false,
	hideRank: false,
});
const ASSETS = `/${ASSETS_DIR}`;
const padNumber = (number: number) => {
	if (number.toString().length < 2) {
		return '0' + number;
	} else {
		return number;
	}
};
const trendDifference = computed(() => {
	if (props.trend === ModuleContentTitleTrend.Up) return `+${props.trendDifference}`;
	if (props.trend === ModuleContentTitleTrend.Stable) return '—';
	if (props.trend === ModuleContentTitleTrend.Down) return `${props.trendDifference}`;

	return '—';
});
const showArrow = computed(() => {
	return props.trend && !isNew.value;
});
const arrowClasses = computed(() => {
	if (!showArrow) {
		return null;
	}
	return {
		'arrow-container__arrow': !!props.trend,
		'arrow-container__arrow--up': props.trend === ModuleContentTitleTrend.Up,
		'arrow-container__arrow--stable': props.trend === ModuleContentTitleTrend.Stable,
		'arrow-container__arrow--down': props.trend === ModuleContentTitleTrend.Down,
	};
});
const isNew = computed(() => {
	return (
		(props.daysInTop1000 === 0 && props.rank <= 1000) ||
		props.trend === ModuleContentTitleTrend.New ||
		props.trendDifference >= 5000
	);
});
const showDetails = computed(() => {
	return props.runtime && props.releaseYear;
});
const runtime = computed(() => {
	const runtimeValue = props.runtime!;
	const { translationKey, minutes, hours } = formatRuntime(runtimeValue);

	return t(translationKey, { minutes, hours }) as string;
});
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.title-ranking-list {
	display: flex;
	justify-content: flex-start;
	align-items: center;

	&__img {
		height: 16px;
		width: 16px;
	}

	&__rank {
		margin: 0 8px;
		font-size: 22px;
		color: #c6c8cd;

		&--condensed {
			font-size: 16px;
			font-weight: 900;
			letter-spacing: 0.24px;
		}
	}

	&__details {
		display: flex;
		align-items: center;

		p,
		span {
			margin-inline-start: 8px;
		}
	}
}

.arrow-container {
	margin-inline-end: auto;
	display: flex;
	justify-content: center;
	align-items: center;
	border: 1px solid;
	border-radius: 16px;
	padding: 2px 5px;
	width: fit-content;

	p {
		font-size: 12px;
		margin: 0;
	}

	svg {
		margin-inline-end: 4px;
		font-size: 14px;
	}

	&--up {
		color: $jw-color-green;
		border-color: $jw-color-green;

		p {
			color: $jw-color-green;
		}
	}

	&--stable {
		color: #797979;
		border-color: #797979;

		p {
			color: #797979;
			font-weight: 900;
			padding: 0 8px;
		}
	}

	&--down {
		color: $jw-color-red;
		border-color: $jw-color-red;

		p {
			color: $jw-color-red;
		}
	}

	&__arrow {
		font-size: 24px;
		margin-inline-end: 4px;

		&--up {
			color: $jw-color-green;
		}

		&--stable {
			color: #797979;
			transform: rotate(90deg);
			display: none;
		}

		&--down {
			color: $jw-color-red;
			transform: rotate(180deg);
		}
	}
}

.new {
	text-transform: uppercase;
	color: #fbc500;
	font-size: 16px;
	font-weight: 400;
	letter-spacing: 0.24px;
}
</style>
